import { Ruler } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { setIdRenaming } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    CreateRulerArgs,
    getDefaultRulerCreateArgs,
    UpdateRulerArgs,
} from "@nova/serializer/src/client/validators/cubes/ruler"
import { selectSingleLayer } from "panels/content/side-panel/slice.new"
import { centroid, maybeSetProjectLocation } from "../projectLocation"

const {
    create: createRuler,
    update: updateRuler,
    delete: deleteRuler,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    Ruler, // entity type
    CreateRulerArgs, // create args to send to server
    ["coordinates", "geometryType", "distanceUnit", "areaUnit", "parentId", "createdBy"], // create args required by action
    UpdateRulerArgs, // update args,
    { name?: string; index?: string },
    { index?: string }
>("Ruler")

export const addRulersToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            // prototodo: fix circular dependency
            const rulers = state.project.data.rulers
            const name = context?.name || `Measurement ${Object.values(rulers).length + 1}`
            const optimisticData = {
                name,
                id: randomUUID(),
                ...getDefaultRulerCreateArgs(store.getState().project.data),
                ...data,
            }
            if (context?.index) optimisticData.index = context.index
            createRuler(
                socket,
                {
                    ...data,
                    name,
                    id: optimisticData.id,
                },
                { index: optimisticData.index }
            )
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["rulers"],
                })
            )
            const project = store.getState().project.data
            const center = centroid(optimisticData.coordinates)
            maybeSetProjectLocation(store, project.orgId, project.id, center)

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))

            // Only enter renaming state if an index was not provided manually.
            // Index will be provided manually if a different geometry type is converted to a line.
            if (!context?.index) {
                // Enter layer renaming state in the Inspector Panel.
                store.dispatch(setIdRenaming(`inspector-layer-title-${optimisticData.id}`))
            }
        })
        .addCase(updateAction, (store, { payload: { id, updates } }, socket) => {
            updateRuler(socket, id, updates, {})
            store.dispatch(updateEntitySocket({ updates, path: ["rulers", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            deleteRuler(socket, id, {})
            store.dispatch(deleteEntitySocket({ path: ["rulers", { id }] }))
        })

export { createAction as createRuler, updateAction as updateRuler, deleteAction as deleteRuler }
